import React, { useState, useEffect } from 'react';

//Material UI components
import { TextField, InputAdornment } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';

/**
 * SearchBar component for filtering deficiencies
 * Provides real-time search functionality with debounced updates
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onSearch - Callback fired when search query changes
 * @param {string} [props.initialValue=''] - Initial search query value
 * @returns {React.ReactElement} Search input field
 */
const SearchBar = ({ onSearch, initialValue = '' }) => {
    const theme = useTheme();

    // Local state for the search text
    const [searchText, setSearchText] = useState(initialValue);

    // Update local state when initialValue changes (from context)
    useEffect(() => {
        setSearchText(initialValue);
    }, [initialValue]);

    /**
     * Handle changes to the search input
     * Updates local state and calls the onSearch callback
     *
     * @param {React.ChangeEvent<HTMLInputElement>} event - Change event
     */
    const handleChange = (event) => {
        const newValue = event.target.value;
        setSearchText(newValue);
        onSearch(newValue);
    };

    return (
        <TextField
            fullWidth
            placeholder="Search deficiencies..."
            value={searchText}
            onChange={handleChange}
            sx={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                    },
                },
            }}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: theme.palette.grey[500] }} />
                        </InputAdornment>
                    ),
                },
            }}
        />
    );
};

export default SearchBar;
